import { useContext, useLayoutEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from 'app/auth';
import {
  buildMarketplaceResumeUrl,
  writeMarketplaceInstallationState,
} from '../../components/GithubPublicAppInstallationDialog/utils';
import { Loader } from '../../components/Loader';
import { LOCAL_STORAGE_KEYS } from '../../consts/localStorage';
import { useUser } from '../../hooks/useUser';
import { QUERY_PARAMS, ROUTES } from '../consts';

/**
 * Protected route that gates the GitHub Marketplace install wizard.
 *
 * The BE sends marketplace-originated visitors here with `installation_id` and
 * `state` query params. This component is the single decision point for the
 * marketplace flow: validate the payload, persist it for refresh / auth-bounce
 * recovery, then route based on auth state.
 *
 * - Authenticated + wizard available → forward straight to the wizard's
 *   "Installation complete" step (step 2) with marketplace query params, which
 *   triggers the `POST /mt/installation/create` mutation in the dialog.
 * - Authenticated + wizard unavailable (feature flag off) → forward to root.
 * - Unauthenticated → seed `redirectTo` with the wizard URL and forward to
 *   `/signin`. After login (and onboarding for new users) the existing
 *   redirect/onboarding-resume mechanisms drop the user back at step 2.
 */
export const GithubMarketplaceWizardProtectedRoute = () => {
  const { user: authUser, isInitialized } = useContext(AuthContext);
  const {
    isGithubPublicAppInstallationWizardAvailable,
    isGithubPublicAppInstallationWizardLoading,
  } = useUser();
  const isAuthenticated = !!authUser;
  const [searchParams] = useSearchParams();

  const installationIdRaw = searchParams.get(QUERY_PARAMS.INSTALLATION_ID);
  const stateRaw = searchParams.get(QUERY_PARAMS.STATE);
  const installationId = Number(installationIdRaw);
  const hasValidPayload =
    !!installationIdRaw && Number.isFinite(installationId) && !!stateRaw;

  const target = hasValidPayload
    ? buildMarketplaceResumeUrl({
        installationId,
        state: stateRaw as string,
        createdAt: new Date().toISOString(),
      })
    : undefined;

  useLayoutEffect(() => {
    if (!hasValidPayload || !target) {
      return;
    }

    writeMarketplaceInstallationState({
      installationId,
      state: stateRaw as string,
    });

    if (isInitialized && !isAuthenticated) {
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.REDIRECT_TO,
        JSON.stringify(target),
      );
    }
  }, [
    hasValidPayload,
    target,
    isInitialized,
    isAuthenticated,
    installationId,
    stateRaw,
  ]);

  if (!hasValidPayload) {
    return <Navigate to={ROUTES.ROOT} replace />;
  }

  if (!isInitialized) {
    return <Loader center />;
  }

  if (isAuthenticated) {
    if (isGithubPublicAppInstallationWizardLoading) {
      return <Loader center />;
    }

    if (!isGithubPublicAppInstallationWizardAvailable) {
      return <Navigate to={ROUTES.ROOT} replace />;
    }

    return <Navigate to={target as string} replace />;
  }

  return <Navigate to={ROUTES.SIGNIN} replace />;
};
